import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  CircularProgress,
  Divider,
  useTheme
} from '@mui/material';
import { Helmet } from 'react-helmet';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import Diversity3Icon from '@mui/icons-material/Diversity3';
import SpeakerNotesIcon from '@mui/icons-material/SpeakerNotes';
import InventoryIcon from '@mui/icons-material/Inventory';
import PersonIcon from '@mui/icons-material/Person';
import VerifiedIcon from '@mui/icons-material/Verified';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FilterListIcon from '@mui/icons-material/FilterList';
import Pagination from '@mui/material/Pagination';
import ClearIcon from '@mui/icons-material/Clear';
import HandymanIcon from '@mui/icons-material/Handyman';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import BusinessIcon from '@mui/icons-material/Business';

import Toast from '../components/Toast';

const ApprovalCenter = ({ hideHeader = false, dedicatedModule = null }) => {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({
    totalPending: 0,
    prayers: 0,
    approvedPrayers: 0,
    declinedPrayers: 0,
    totalPrayers: 0,
    testimonies: 0,
    giveItems: 0,
    itemApplications: 0,
    itemRequests: 0,
    users: 0
  });

  // Filters
  const [statusFilter, setStatusFilter] = useState('pending'); // 'pending', 'approved', 'declined', 'all'
  const [moduleFilter, setModuleFilter] = useState(dedicatedModule || 'all');     // 'all', 'prayers', 'testimonies', 'give_items', 'item_requests', 'users'
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Modals
  const [viewItem, setViewItem] = useState(null);
  const [declineItem, setDeclineItem] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const rawApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/admin/';
  const cleanBaseUrl = rawApiUrl.replace(/\/+$/, '') + '/';

  // Fetch live counts
  const fetchCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${cleanBaseUrl}approval-counts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setCounts({
            totalPending: data.totalPending ?? (data.counts?.totalPending || 0),
            ...(data.counts || {})
          });
        }
      }
    } catch (e) {
      console.warn('Failed to fetch approval counts:', e.message);
    }
  }, [cleanBaseUrl]);

  // Fetch inbox items
  const fetchInbox = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const effectiveModule = dedicatedModule || moduleFilter;
      const res = await fetch(`${cleanBaseUrl}approval-inbox`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          status: statusFilter,
          module: effectiveModule,
          search,
          page,
          perPage
        })
      });

      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setItems(data.data || []);
          setTotalItems(data.pagination?.total || 0);
          setTotalPages(data.pagination?.totalPages || 1);
        }
      }
    } catch (e) {
      console.warn('Failed to fetch approval inbox:', e.message);
    } finally {
      setLoading(false);
    }
  }, [cleanBaseUrl, statusFilter, moduleFilter, search, page, perPage]);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  useEffect(() => {
    fetchInbox();
  }, [fetchInbox]);

  const handleDecision = async (moduleType, itemId, decision, reason = '') => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${cleanBaseUrl}approval-decision`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          moduleType,
          itemId,
          decision,
          reason
        })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setToast({
          open: true,
          message: data.message || `Request successfully ${decision === 'approve' ? 'approved' : 'declined'}!`,
          severity: 'success'
        });
        setDeclineItem(null);
        setDeclineReason('');
        setViewItem(null);
        fetchCounts();
        fetchInbox();
      } else {
        setToast({
          open: true,
          message: data.message || 'Failed to process decision',
          severity: 'error'
        });
      }
    } catch (err) {
      setToast({ open: true, message: err.message, severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const getModuleBadge = (type) => {
    switch (type) {
      case 'prayer':
        return { label: 'Prayer Request', icon: <Diversity3Icon sx={{ fontSize: 14 }} />, color: '#0D9488', bg: 'rgba(13, 148, 136, 0.12)', border: 'rgba(13, 148, 136, 0.35)' };
      case 'testimony':
        return { label: 'Testimony', icon: <SpeakerNotesIcon sx={{ fontSize: 14 }} />, color: '#8B5CF6', bg: 'rgba(139, 92, 246, 0.12)', border: 'rgba(139, 92, 246, 0.35)' };
      case 'give_item':
        return { label: 'Share & Accept', icon: <InventoryIcon sx={{ fontSize: 14 }} />, color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.12)', border: 'rgba(245, 158, 11, 0.35)' };
      case 'item_application':
      case 'item_request':
        return { label: 'Need / Item Request', icon: <VolunteerActivismIcon sx={{ fontSize: 14 }} />, color: '#EC4899', bg: 'rgba(236, 72, 153, 0.12)', border: 'rgba(236, 72, 153, 0.35)' };
      case 'user_verification':
      case 'user_account':
        return { label: 'User Verification', icon: <VerifiedIcon sx={{ fontSize: 14 }} />, color: '#3B82F6', bg: 'rgba(59, 130, 246, 0.12)', border: 'rgba(59, 130, 246, 0.35)' };
      default:
        return { label: type, icon: <PendingActionsIcon sx={{ fontSize: 14 }} />, color: '#64748B', bg: 'rgba(100, 116, 139, 0.12)', border: 'rgba(100, 116, 139, 0.35)' };
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'Approved':
        return (
          <Chip
            size="small"
            icon={<CheckCircleIcon sx={{ fontSize: '13px !important', color: '#10B981 !important' }} />}
            label="Approved"
            sx={{ bgcolor: 'rgba(16, 185, 129, 0.12)', color: '#10B981', border: '1px solid rgba(16, 185, 129, 0.35)', fontWeight: 700, fontSize: '0.75rem' }}
          />
        );
      case 'Declined':
        return (
          <Chip
            size="small"
            icon={<CancelIcon sx={{ fontSize: '13px !important', color: '#EF4444 !important' }} />}
            label="Declined"
            sx={{ bgcolor: 'rgba(239, 68, 68, 0.12)', color: '#EF4444', border: '1px solid rgba(239, 68, 68, 0.35)', fontWeight: 700, fontSize: '0.75rem' }}
          />
        );
      default:
        return (
          <Chip
            size="small"
            icon={<WarningAmberIcon sx={{ fontSize: '13px !important', color: '#F59E0B !important' }} />}
            label="Pending Decision"
            sx={{ bgcolor: 'rgba(245, 158, 11, 0.14)', color: '#F59E0B', border: '1px solid rgba(245, 158, 11, 0.4)', fontWeight: 700, fontSize: '0.75rem' }}
          />
        );
    }
  };

  const CARD_BG = isLight ? '#FFFFFF' : 'rgba(21, 34, 46, 0.7)';
  const BORDER_COLOR = isLight ? '#E2E8F0' : 'rgba(76, 161, 175, 0.25)';
  const TEXT_MAIN = isLight ? '#0F172A' : '#FFFFFF';
  const TEXT_MUTED = isLight ? '#64748B' : 'rgba(255, 255, 255, 0.65)';

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
      <Helmet><title>Approval Center - GPN</title></Helmet>

      {/* Global CSS for pulsing badges */}
      <style>{`
        @keyframes pulsePendingBadge {
          0%, 100% { transform: scale(1); opacity: 1; box-shadow: 0 0 10px rgba(220, 38, 38, 0.6); }
          50% { transform: scale(1.08); opacity: 0.85; box-shadow: 0 0 16px rgba(220, 38, 38, 1); }
        }
      `}</style>

      {/* Header with Title and Pending Numbers on the RIGHT side */}
      <Box sx={{ mb: 2.5, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, rgba(13, 148, 136, 0.3) 0%, rgba(76, 161, 175, 0.2) 100%)',
              border: '1px solid rgba(76, 161, 175, 0.4)',
              color: '#64d2e2'
            }}
          >
            <PendingActionsIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: TEXT_MAIN, letterSpacing: '-0.3px', fontSize: { xs: '1.25rem', md: '1.45rem' } }}>
              {dedicatedModule === 'prayers' ? 'Prayer Approval Center' : 'Approval Center'}
            </Typography>

            {/* Pending count prominently displayed on the RIGHT side of the title */}
            {((dedicatedModule === 'prayers' ? (counts.prayers || 0) : counts.totalPending) > 0) && (
              <Box
                sx={{
                  bgcolor: '#DC2626',
                  color: '#FFFFFF',
                  borderRadius: '16px',
                  px: 1.3,
                  py: 0.4,
                  fontSize: '0.8rem',
                  fontWeight: 800,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.6,
                  animation: 'pulsePendingBadge 2s infinite ease-in-out',
                  letterSpacing: '0.2px'
                }}
              >
                <span>{dedicatedModule === 'prayers' ? counts.prayers : counts.totalPending}</span>
                <span style={{ fontSize: '0.72rem', fontWeight: 600, opacity: 0.9 }}>Pending</span>
              </Box>
            )}
          </Box>
        </Box>

        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={() => { fetchCounts(); fetchInbox(); }}
          sx={{
            borderColor: 'rgba(76, 161, 175, 0.4)',
            color: '#64d2e2',
            bgcolor: 'rgba(76, 161, 175, 0.08)',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 2,
            py: 0.6,
            '&:hover': { borderColor: '#64d2e2', bgcolor: 'rgba(76, 161, 175, 0.2)' }
          }}
        >
          Refresh Inbox
        </Button>
      </Box>

      {/* KPI Cards: Sleek Glassmorphism Metric Tiles */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {(dedicatedModule === 'prayers' ? [
          { label: 'Pending Moderation', count: counts.prayers || 0, color: '#EF4444', icon: <PendingActionsIcon sx={{ fontSize: 20 }} />, status: 'pending', activeGlow: true },
          { label: 'Approved Prayers', count: counts.approvedPrayers || 0, color: '#10B981', icon: <CheckCircleIcon sx={{ fontSize: 20 }} />, status: 'approved' },
          { label: 'Declined Prayers', count: counts.declinedPrayers || 0, color: '#F43F5E', icon: <CancelIcon sx={{ fontSize: 20 }} />, status: 'declined' },
          { label: 'All Prayer Petitions', count: counts.totalPrayers || 0, color: '#0D9488', icon: <Diversity3Icon sx={{ fontSize: 20 }} />, status: 'all' },
        ] : [
          { label: 'Total Pending', count: counts.totalPending, color: '#EF4444', icon: <PendingActionsIcon sx={{ fontSize: 20 }} />, mod: 'all', activeGlow: true },
          { label: 'Prayers', count: counts.prayers, color: '#0D9488', icon: <Diversity3Icon sx={{ fontSize: 20 }} />, mod: 'prayers' },
          { label: 'Testimonies', count: counts.testimonies, color: '#8B5CF6', icon: <SpeakerNotesIcon sx={{ fontSize: 20 }} />, mod: 'testimonies' },
          { label: 'Shared Items', count: counts.giveItems, color: '#F59E0B', icon: <InventoryIcon sx={{ fontSize: 20 }} />, mod: 'give_items' },
          { label: 'Item Requests', count: counts.itemRequests + counts.itemApplications, color: '#EC4899', icon: <VolunteerActivismIcon sx={{ fontSize: 20 }} />, mod: 'item_requests' },
          { label: 'User Accounts', count: counts.users, color: '#3B82F6', icon: <HowToRegIcon sx={{ fontSize: 20 }} />, mod: 'users' }
        ]).map((m, idx) => {
          const isSelected = dedicatedModule === 'prayers'
            ? statusFilter === m.status
            : moduleFilter === m.mod && (m.mod !== 'all' || statusFilter === 'pending');
          return (
            <Grid item xs={6} sm={dedicatedModule === 'prayers' ? 3 : 4} md={dedicatedModule === 'prayers' ? 3 : 2} key={idx}>
              <Card
                onClick={() => {
                  if (dedicatedModule === 'prayers') {
                    setStatusFilter(m.status);
                  } else {
                    setModuleFilter(m.mod);
                    if (m.mod === 'all') setStatusFilter('pending');
                  }
                  setPage(1);
                }}
                sx={{
                  bgcolor: CARD_BG,
                  border: isSelected ? `1.5px solid ${m.color}` : `1px solid ${BORDER_COLOR}`,
                  borderRadius: 2.5,
                  p: 1.5,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  backdropFilter: 'blur(10px)',
                  boxShadow: isSelected ? `0 4px 16px ${m.color}33` : 'none',
                  '&:hover': {
                    borderColor: m.color,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 14px ${m.color}26`
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: `${m.color}1A`,
                      color: m.color
                    }}
                  >
                    {m.icon}
                  </Box>
                  {m.activeGlow && m.count > 0 && (
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#EF4444', boxShadow: '0 0 8px #EF4444' }} />
                  )}
                </Box>
                <Typography variant="caption" sx={{ color: TEXT_MUTED, fontWeight: 600, display: 'block', fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {m.label}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: m.color, mt: 0.3, lineHeight: 1.1 }}>
                  {m.count}
                </Typography>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Control Bar: Two-tier clean layout */}
      <Paper
        sx={{
          bgcolor: CARD_BG,
          border: `1px solid ${BORDER_COLOR}`,
          borderRadius: 2.5,
          p: 2,
          mb: 2.5,
          backdropFilter: 'blur(10px)'
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          {/* Status Decision Pills */}
          <Box sx={{ display: 'inline-flex', bgcolor: isLight ? '#F1F5F9' : 'rgba(255, 255, 255, 0.05)', p: 0.5, borderRadius: 2, border: `1px solid ${BORDER_COLOR}` }}>
            {[
              { key: 'pending', label: 'Pending', count: dedicatedModule === 'prayers' ? counts.prayers : counts.totalPending },
              { key: 'approved', label: 'Approved' },
              { key: 'declined', label: 'Declined' },
              { key: 'all', label: 'All History' }
            ].map(st => {
              const active = statusFilter === st.key;
              return (
                <Button
                  key={st.key}
                  size="small"
                  onClick={() => { setStatusFilter(st.key); setPage(1); }}
                  sx={{
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: '0.825rem',
                    px: 2,
                    py: 0.6,
                    color: active ? '#FFFFFF' : TEXT_MUTED,
                    bgcolor: active ? (st.key === 'pending' ? '#DC2626' : (st.key === 'approved' ? '#10B981' : (st.key === 'declined' ? '#EF4444' : '#177373'))) : 'transparent',
                    boxShadow: active ? '0 2px 8px rgba(0,0,0,0.25)' : 'none',
                    '&:hover': {
                      bgcolor: active ? undefined : (isLight ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.08)'),
                      color: '#FFFFFF'
                    }
                  }}
                >
                  {st.label}
                  {st.count !== undefined && st.count > 0 && (
                    <Box
                      component="span"
                      sx={{
                        ml: 0.8,
                        bgcolor: active ? 'rgba(255,255,255,0.25)' : '#DC2626',
                        color: '#FFFFFF',
                        borderRadius: '10px',
                        px: 0.7,
                        py: 0.1,
                        fontSize: '0.68rem',
                        fontWeight: 800
                      }}
                    >
                      {st.count}
                    </Box>
                  )}
                </Button>
              );
            })}
          </Box>

          {/* Search Box */}
          <Box sx={{ flex: { xs: '1 1 100%', md: '0 1 340px' } }}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search by title, believer name, email..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#64d2e2', fontSize: 18 }} />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')}>
                      <ClearIcon sx={{ fontSize: 16, color: TEXT_MUTED }} />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: isLight ? '#F8FAFC' : 'rgba(255, 255, 255, 0.03)',
                  color: TEXT_MAIN,
                  borderColor: BORDER_COLOR,
                  '& fieldset': { borderColor: BORDER_COLOR },
                  '&:hover fieldset': { borderColor: '#64d2e2' },
                  '&.Mui-focused fieldset': { borderColor: '#64d2e2' }
                }
              }}
            />
          </Box>
        </Box>

        {/* Tier 2: Category Chips (Hidden when dedicated to Prayers) */}
        {!dedicatedModule && (
          <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pt: 1.5, mt: 1.5, borderTop: `1px solid ${BORDER_COLOR}` }}>
            <Typography variant="caption" sx={{ color: TEXT_MUTED, fontWeight: 700, alignSelf: 'center', mr: 0.5, textTransform: 'uppercase', fontSize: '0.7rem' }}>
              Filter Module:
            </Typography>
            {[
              { key: 'all', label: 'All Modules' },
              { key: 'prayers', label: 'Prayers' },
              { key: 'testimonies', label: 'Testimonies' },
              { key: 'give_items', label: 'Share & Accept' },
              { key: 'users', label: 'User Accounts' }
            ].map(mod => {
              const active = moduleFilter === mod.key;
              return (
                <Chip
                  key={mod.key}
                  label={mod.label}
                  clickable
                  onClick={() => { setModuleFilter(mod.key); setPage(1); }}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.78rem',
                    py: 0.5,
                    bgcolor: active ? 'rgba(76, 161, 175, 0.25)' : 'transparent',
                    color: active ? '#64d2e2' : TEXT_MUTED,
                    border: `1px solid ${active ? '#64d2e2' : BORDER_COLOR}`,
                    '&:hover': {
                      bgcolor: 'rgba(76, 161, 175, 0.15)',
                      color: '#64d2e2'
                    }
                  }}
                />
              );
            })}
          </Box>
        )}
      </Paper>

      {/* Main Inbox Table */}
      <Paper
        sx={{
          bgcolor: CARD_BG,
          border: `1px solid ${BORDER_COLOR}`,
          borderRadius: 2.5,
          overflow: 'hidden',
          boxShadow: isLight ? '0 4px 16px rgba(0,0,0,0.06)' : 'none',
          backdropFilter: 'blur(10px)'
        }}
      >
        <TableContainer sx={{ overflowX: { xs: "hidden", lg: "auto" } }}>
          <Table sx={{ width: "100%", minWidth: 0, tableLayout: "fixed" }}>
            <TableHead sx={{ bgcolor: isLight ? '#F1F5F9' : 'rgba(0, 0, 0, 0.35)' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, color: '#64d2e2', py: 1.8, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {dedicatedModule === 'prayers' ? 'Prayer Title & Petition' : 'Submission Details'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#64d2e2', py: 1.8, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {dedicatedModule === 'prayers' ? 'Category & Visibility' : 'Module & Type'}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#64d2e2', py: 1.8, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Believer / Requester
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#64d2e2', py: 1.8, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Organization
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#64d2e2', py: 1.8, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Location
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#64d2e2', py: 1.8, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Submitted At
                </TableCell>
                <TableCell sx={{ fontWeight: 700, color: '#64d2e2', py: 1.8, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Status
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: '#64d2e2', py: 1.8, fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px', pr: 3 }}>
                  Decision Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={36} sx={{ color: '#64d2e2' }} />
                    <Typography variant="body2" sx={{ mt: 1.5, color: TEXT_MUTED }}>
                      {dedicatedModule === 'prayers' ? 'Loading prayer requests for pastoral review...' : 'Loading pastoral approval inbox...'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 9 }}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        bgcolor: 'rgba(16, 185, 129, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 1.5,
                        color: '#10B981'
                      }}
                    >
                      <CheckCircleIcon sx={{ fontSize: 36 }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: TEXT_MAIN }}>
                      {dedicatedModule === 'prayers' ? 'No Pending Prayer Requests' : 'Inbox is Clear!'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: TEXT_MUTED, mt: 0.5, maxWidth: 460, mx: 'auto' }}>
                      {dedicatedModule === 'prayers'
                        ? 'All prayer requests have been reviewed and approved. When believers submit new prayers requiring moderation, they will appear here.'
                        : 'There are currently no pending submissions matching your active filter criteria.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                items.map(item => {
                  const modBadge = getModuleBadge(item.moduleType);
                  return (
                    <TableRow
                      key={item.id}
                      hover
                      sx={{
                        '&:hover': { bgcolor: isLight ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)' },
                        borderBottom: `1px solid ${BORDER_COLOR}`
                      }}
                    >
                      {/* Submission Details */}
                      <TableCell sx={{ maxWidth: 320 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: TEXT_MAIN, mb: 0.4 }}>
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: TEXT_MUTED,
                            fontSize: '0.82rem',
                            lineHeight: 1.4,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                        >
                          {item.contentPreview}
                        </Typography>
                      </TableCell>

                      {/* Module & Type / Category & Visibility */}
                      <TableCell sx={{ minWidth: 160 }}>
                        {dedicatedModule === 'prayers' ? (
                          <>
                            <Chip
                              icon={<Diversity3Icon sx={{ fontSize: 14 }} />}
                              label={item.categoryName || 'General Prayer'}
                              size="small"
                              sx={{
                                bgcolor: 'rgba(13, 148, 136, 0.15)',
                                color: '#64d2e2',
                                border: '1px solid rgba(76, 161, 175, 0.4)',
                                fontWeight: 700,
                                fontSize: '0.74rem'
                              }}
                            />
                            {item.visibility && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: item.visibility === 'Elders Only' ? '#F59E0B' : (item.visibility === 'Pastoral Private' ? '#A78BFA' : '#10B981'),
                                  display: 'block',
                                  mt: 0.5,
                                  pl: 0.5,
                                  fontWeight: 600
                                }}
                              >
                                {item.visibility}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <>
                            <Chip
                              icon={modBadge.icon}
                              label={modBadge.label}
                              size="small"
                              sx={{
                                bgcolor: modBadge.bg,
                                color: modBadge.color,
                                border: `1px solid ${modBadge.border}`,
                                fontWeight: 700,
                                fontSize: '0.74rem'
                              }}
                            />
                            {item.categoryName && (
                              <Typography variant="caption" sx={{ color: TEXT_MUTED, display: 'block', mt: 0.5, pl: 0.5 }}>
                                Category: {item.categoryName}
                              </Typography>
                            )}
                          </>
                        )}
                      </TableCell>

                      {/* Believer / Requester */}
                      <TableCell sx={{ minWidth: 200 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                          <Avatar
                            src={item.userAvatar}
                            sx={{
                              width: 34,
                              height: 34,
                              bgcolor: 'rgba(76, 161, 175, 0.25)',
                              color: '#64d2e2',
                              fontWeight: 700,
                              fontSize: '0.85rem'
                            }}
                          >
                            {item.userName ? item.userName.charAt(0).toUpperCase() : 'U'}
                          </Avatar>
                          <Box sx={{ overflow: 'hidden' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: TEXT_MAIN, lineHeight: 1.2 }}>
                              {item.userName}
                            </Typography>
                            {item.userEmail && (
                              <Typography variant="caption" sx={{ color: TEXT_MUTED, display: 'block', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: 170 }}>
                                {item.userEmail}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Organization */}
                      <TableCell sx={{ minWidth: 160 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <BusinessIcon sx={{ fontSize: 16, color: '#64d2e2', flexShrink: 0 }} />
                          <Typography variant="body2" sx={{ color: TEXT_MAIN, fontWeight: 600, fontSize: '0.82rem' }}>
                            {item.organizationName || 'Global / Network'}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Location */}
                      <TableCell sx={{ minWidth: 150 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                          <LocationOnIcon sx={{ fontSize: 15, color: '#64d2e2', flexShrink: 0 }} />
                          <Typography variant="caption" sx={{ color: TEXT_MUTED, fontWeight: 500 }}>
                            {item.location || 'Location unavailable'}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Submitted At */}
                      <TableCell sx={{ minWidth: 140 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                          <AccessTimeIcon sx={{ fontSize: 14, color: TEXT_MUTED, flexShrink: 0 }} />
                          <Typography variant="caption" sx={{ color: TEXT_MAIN, fontWeight: 500 }}>
                            {item.createdAt ? new Date(item.createdAt).toLocaleString(undefined, {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Status */}
                      <TableCell sx={{ minWidth: 130 }}>
                        {getStatusChip(item.status)}
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right" sx={{ pr: 3, whiteSpace: 'nowrap' }}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                          <Tooltip title="View Submission Details">
                            <IconButton
                              size="small"
                              onClick={() => setViewItem(item)}
                              sx={{
                                color: '#64d2e2',
                                bgcolor: 'rgba(76, 161, 175, 0.12)',
                                border: '1px solid rgba(76, 161, 175, 0.3)',
                                '&:hover': { bgcolor: 'rgba(76, 161, 175, 0.25)', borderColor: '#64d2e2' }
                              }}
                            >
                              <VisibilityIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>

                          {item.status === 'Pending' && (
                            <>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<CheckCircleIcon sx={{ fontSize: 15 }} />}
                                onClick={() => handleDecision(item.moduleType, item.rawId, 'approve')}
                                disabled={actionLoading}
                                sx={{
                                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                                  color: '#FFFFFF',
                                  textTransform: 'none',
                                  fontWeight: 700,
                                  fontSize: '0.78rem',
                                  px: 1.8,
                                  py: 0.5,
                                  borderRadius: 2,
                                  boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                                  '&:hover': { background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' }
                                }}
                              >
                                Approve
                              </Button>

                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<CancelIcon sx={{ fontSize: 15 }} />}
                                onClick={() => setDeclineItem(item)}
                                disabled={actionLoading}
                                sx={{
                                  borderColor: 'rgba(239, 68, 68, 0.5)',
                                  color: '#F87171',
                                  bgcolor: 'rgba(239, 68, 68, 0.08)',
                                  textTransform: 'none',
                                  fontWeight: 700,
                                  fontSize: '0.78rem',
                                  px: 1.6,
                                  py: 0.5,
                                  borderRadius: 2,
                                  '&:hover': { borderColor: '#EF4444', bgcolor: 'rgba(239, 68, 68, 0.18)' }
                                }}
                              >
                                Decline
                              </Button>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderTop: `1px solid ${BORDER_COLOR}` }}>
            <Typography variant="caption" sx={{ color: TEXT_MUTED }}>
              Showing {items.length} of {totalItems} items
            </Typography>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, p) => setPage(p)}
              sx={{
                '& .MuiPaginationItem-root': {
                  color: TEXT_MAIN,
                  borderColor: BORDER_COLOR,
                  '&.Mui-selected': {
                    bgcolor: '#177373',
                    color: '#FFFFFF',
                    fontWeight: 700
                  }
                }
              }}
            />
          </Box>
        )}
      </Paper>

      {/* View Submission Modal */}
      <Dialog
        open={Boolean(viewItem)}
        onClose={() => setViewItem(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#15222E',
            border: '1px solid rgba(76, 161, 175, 0.4)',
            borderRadius: 3,
            color: '#FFFFFF'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: `1px solid ${BORDER_COLOR}`, pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <VisibilityIcon sx={{ color: '#64d2e2' }} />
            <Typography variant="h6" fontWeight={700}>
              Review Submission Details
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setViewItem(null)} sx={{ color: TEXT_MUTED }}>
            <ClearIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          {viewItem && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#64d2e2', fontWeight: 700, textTransform: 'uppercase' }}>
                    {getModuleBadge(viewItem.moduleType).label}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.3 }}>
                    {viewItem.title}
                  </Typography>
                  {viewItem.createdAt && (
                    <Typography variant="caption" sx={{ color: TEXT_MUTED, display: 'block', mt: 0.3 }}>
                      Submitted: {new Date(viewItem.createdAt).toLocaleString(undefined, {
                        month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}
                    </Typography>
                  )}
                </Box>
                {getStatusChip(viewItem.status)}
              </Box>

              <Divider sx={{ borderColor: BORDER_COLOR }} />

              {/* Requester Info Card */}
              <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'rgba(255, 255, 255, 0.03)', border: `1px solid ${BORDER_COLOR}` }}>
                <Typography variant="caption" sx={{ color: '#64d2e2', fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 1 }}>
                  Submitter / Believer Profile
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar src={viewItem.userAvatar} sx={{ width: 44, height: 44, bgcolor: '#177373', fontWeight: 700 }}>
                    {viewItem.userName?.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {viewItem.userName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: TEXT_MUTED, display: 'block' }}>
                      {viewItem.userEmail} {viewItem.userPhone ? `• ${viewItem.userPhone}` : ''}
                    </Typography>
                    <Typography variant="caption" sx={{ color: TEXT_MUTED, display: 'block' }}>
                      📍 {viewItem.userLocation || viewItem.location || 'Location unavailable'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64d2e2', display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.4, fontWeight: 600 }}>
                      <BusinessIcon sx={{ fontSize: 13 }} /> {viewItem.organizationName || 'Global / Network'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Full Content Preview */}
              <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: 'rgba(255, 255, 255, 0.03)', border: `1px solid ${BORDER_COLOR}` }}>
                <Typography variant="caption" sx={{ color: '#64d2e2', fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 1 }}>
                  Full Submission Content
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, color: 'rgba(255, 255, 255, 0.9)' }}>
                  {viewItem.description || viewItem.fullContent || viewItem.contentPreview}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${BORDER_COLOR}`, gap: 1.5 }}>
          {viewItem?.status === 'Pending' && (
            <>
              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => {
                  setDeclineItem(viewItem);
                  setViewItem(null);
                }}
                disabled={actionLoading}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
              >
                Decline Request
              </Button>
              <Button
                variant="contained"
                startIcon={<CheckCircleIcon />}
                onClick={() => handleDecision(viewItem.moduleType, viewItem.rawId, 'approve')}
                disabled={actionLoading}
                sx={{
                  bgcolor: '#10B981',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 3,
                  '&:hover': { bgcolor: '#059669' }
                }}
              >
                Approve Submission
              </Button>
            </>
          )}
          <Button onClick={() => setViewItem(null)} sx={{ color: TEXT_MUTED, textTransform: 'none' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Decline Reason Modal */}
      <Dialog
        open={Boolean(declineItem)}
        onClose={() => setDeclineItem(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#15222E',
            border: '1px solid rgba(239, 68, 68, 0.4)',
            borderRadius: 3,
            color: '#FFFFFF'
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${BORDER_COLOR}`, pb: 2 }}>
          <CancelIcon sx={{ color: '#EF4444' }} />
          <Typography variant="h6" fontWeight={700}>
            Decline Submission
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ py: 3 }}>
          {declineItem && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" sx={{ color: TEXT_MUTED }}>
                Are you sure you want to decline <strong>{declineItem.title}</strong> by <strong>{declineItem.userName}</strong>?
              </Typography>

              <Box>
                <Typography variant="caption" sx={{ color: '#64d2e2', fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 1 }}>
                  Quick Rejection Reason (Optional):
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mb: 1.5 }}>
                  {[
                    'Inappropriate content',
                    'Insufficient details',
                    'Duplicate submission',
                    'Policy violation'
                  ].map(reason => (
                    <Chip
                      key={reason}
                      label={reason}
                      clickable
                      size="small"
                      onClick={() => setDeclineReason(reason)}
                      sx={{
                        bgcolor: declineReason === reason ? 'rgba(239, 68, 68, 0.25)' : 'rgba(255,255,255,0.05)',
                        color: declineReason === reason ? '#EF4444' : TEXT_MUTED,
                        border: `1px solid ${declineReason === reason ? '#EF4444' : BORDER_COLOR}`
                      }}
                    />
                  ))}
                </Box>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Enter custom rejection reason..."
                  value={declineReason}
                  onChange={e => setDeclineReason(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'rgba(255, 255, 255, 0.04)',
                      color: '#FFFFFF',
                      '& fieldset': { borderColor: BORDER_COLOR }
                    }
                  }}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${BORDER_COLOR}`, gap: 1.5 }}>
          <Button onClick={() => setDeclineItem(null)} sx={{ color: TEXT_MUTED, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => handleDecision(declineItem.moduleType, declineItem.rawId, 'decline', declineReason)}
            disabled={actionLoading}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, px: 3 }}
          >
            Confirm Decline
          </Button>
        </DialogActions>
      </Dialog>

      <Toast toast={toast} setToast={setToast} />
    </Box>
  );
};

export default ApprovalCenter;
